// @import
import { autoUpdate, computePosition, flip, type Placement } from "@floating-ui/dom";
import { ensureHTMLElement, ensureHTMLNodeList } from "@src/js/lib/ensure";
import { viewport } from "@src/js/lib/viewport";
import Alpine from "alpinejs";

const placements = [
  "top",
  "right",
  "bottom",
  "left",
  "top-start",
  "top-end",
  "right-start",
  "right-end",
  "bottom-start",
  "bottom-end",
  "left-start",
  "left-end",
];

Alpine.directive("dropdown", (element, { modifiers }, { cleanup, effect }) => {
  const ensurePlacement = (str: string): str is Placement => placements.includes(str);
  const panelPlacementDefault = modifiers.length ? modifiers.join("-") : "top";

  const dom = {
    panel: ensureHTMLElement(element.querySelector(".c-dropdown__panel")),
    trigger: ensureHTMLElement(element.querySelector(".c-dropdown__trigger")),
    options: ensureHTMLNodeList(element.querySelectorAll(".c-button.c-dropdown__option")),
  };

  function closePanel() {
    dom.panel.classList.remove("c-dropdown__panel--open");
  }

  function togglePanel() {
    dom.panel.classList.toggle("c-dropdown__panel--open");
  }

  // Close panel on outside click
  function onClickOutside(event: MouseEvent) {
    if (!element.contains(event.target as Node)) {
      closePanel();
    }
  }

  // Auto update panel position
  const cleanupPanelPostionUpdate = autoUpdate(dom.trigger, dom.panel, () => {
    computePosition(dom.trigger, dom.panel, {
      middleware: [flip()],
      placement: ensurePlacement(panelPlacementDefault) ? panelPlacementDefault : "bottom",
    }).then(({ x, y }) => {
      Object.assign(dom.panel.style, {
        left: `${x}px`,
        top: `${y}px`,
      });
    });
  });

  // Replace trigger label on option click
  function handleOptionClick(event: MouseEvent) {
    const option = event.currentTarget as HTMLElement;
    const label = option.querySelector(".c-button__label")?.textContent;
    if (!label) return;
    dom.trigger.querySelector(".c-button__label")!.textContent = label;
    dom.panel.style.minWidth = dom.trigger.offsetWidth + "px";
  }

  // Event listeners
  element.addEventListener("click", togglePanel);
  document.addEventListener("click", onClickOutside);
  dom.options.forEach((option) => {
    option.addEventListener("click", handleOptionClick);
  });

  // Reactive panel width
  effect(() => {
    if (viewport.width) {
      dom.panel.style.minWidth = dom.trigger.offsetWidth + "px";
    }
  });

  // Cleanup
  cleanup(() => {
    cleanupPanelPostionUpdate();
    element.removeEventListener("click", togglePanel);
    document.removeEventListener("click", onClickOutside);
    dom.options.forEach((option) => {
      option.removeEventListener("click", handleOptionClick);
    });
  });
});
